import type { ActivityLogicPlugin } from '../../core/types/plugin.base.types';
import type { VinculaPayload, VinculaRuntime } from '../../core/types/payloads/vincula.types';
import { applyStimulusFields } from '../../core/print/blockStimulus';
import type { RoutingMode } from '../../core/types/room.types';
import type { HostActionPayload, StudentInputEnvelope } from '../../core/types/events.types';

export function validateVinculaPayload(raw: unknown): VinculaPayload {
  if (!raw || typeof raw !== 'object') throw new Error('Vincula payload must be an object');
  const data = raw as Partial<VinculaPayload>;
  if (!data.title) throw new Error('Vincula requires title');
  if (!Array.isArray(data.pairs) || data.pairs.length < 2) {
    throw new Error('Vincula requires at least 2 pairs');
  }
  const pairs = data.pairs.map((p, i) => ({
    id: String(p.id || `pair_${i + 1}`),
    ladoA: String(p.ladoA || '').trim(),
    ladoB: String(p.ladoB || '').trim(),
  }));
  const rightColumnOrder = Array.isArray(data.rightColumnOrder)
    ? data.rightColumnOrder.map(String)
    : undefined;
  return applyStimulusFields(
    { title: String(data.title), pairs, rightColumnOrder },
    raw,
    'vincula',
  );
}

export function createInitialVinculaRuntime(_payload: VinculaPayload, _routing: RoutingMode): VinculaRuntime {
  return { links: {}, finalized: {}, aggregates: { submitted: 0, correct: 0 } };
}

function scoreLinks(payload: VinculaPayload, links: Record<string, string>): number {
  let correct = 0;
  for (const pair of payload.pairs) {
    if (links[pair.id] === pair.id) correct += 1;
  }
  return correct;
}

export const vinculaLogic: ActivityLogicPlugin<'vincula'> = {
  mode: 'vincula',
  validatePayload: validateVinculaPayload,
  createInitialRuntime: createInitialVinculaRuntime,

  onStudentInput(ctx, input: StudentInputEnvelope, participant) {
    if (input.action === 'LINK') {
      const data = input.data as { leftId: string; rightId: string };
      const prev = ctx.runtime.links[participant.participantId] ?? {};
      const links = {
        ...ctx.runtime.links,
        [participant.participantId]: { ...prev, [data.leftId]: data.rightId },
      };
      return { runtime: { ...ctx.runtime, links } };
    }
    if (input.action === 'SUBMIT') {
      const links = ctx.runtime.links[participant.participantId] ?? {};
      const correct = scoreLinks(ctx.payload, links);
      const finalized = { ...ctx.runtime.finalized, [participant.participantId]: true };
      const all = Object.keys(finalized).filter((k) => finalized[k]);
      return {
        runtime: {
          ...ctx.runtime,
          finalized,
          aggregates: { submitted: all.length, correct: all.reduce((s, pid) => s + scoreLinks(ctx.payload, ctx.runtime.links[pid] ?? {}), 0) },
        },
        scores: [{ participantId: participant.participantId, delta: correct, reason: 'vincula' }],
      };
    }
    return { runtime: ctx.runtime, rejected: true, rejectReason: 'UNKNOWN_ACTION' };
  },

  onHostAction(ctx, _action: HostActionPayload) {
    return { runtime: ctx.runtime };
  },
};

export function demoVinculaPayload(): VinculaPayload {
  // Neutro — NUNCA tonalidades Do/Sol Mayor (fuga a guías de ritmo vía defaultPayloadForMode).
  return validateVinculaPayload({
    title: 'Relaciona ideas',
    pairs: [
      { id: 'p1', ladoA: 'Concepto A', ladoB: 'Definición A' },
      { id: 'p2', ladoA: 'Concepto B', ladoB: 'Definición B' },
      { id: 'p3', ladoA: 'Concepto C', ladoB: 'Definición C' },
    ],
  });
}
